/**
 * Weekly menu page ([smakota_menu]).
 *
 * Entries come from the "Add to menu" button on recipes (localStorage, see
 * engage.js). Here the visitor spreads them over the week, changes servings,
 * and gets ONE shopping list: ingredients from smakota/v1/ingredients are
 * scaled and added up (same product + unit → one line; the plugin decides
 * which names are the same product). Staples go to "usually at home".
 */

import { readMenu, writeMenu } from './menu-store';
import { formatQty, unitFor } from './qty';

const CHECKED = 'smakota-menu-checked';
const MAX_PORTIONS = 12; // Upper bound: this, or 4× the recipe's yield if larger.

const readChecked = () => {
	try {
		return new Set(
			JSON.parse( window.localStorage.getItem( CHECKED ) || '[]' )
		);
	} catch ( e ) {
		return new Set();
	}
};
const writeChecked = ( set ) => {
	try {
		window.localStorage.setItem( CHECKED, JSON.stringify( [ ...set ] ) );
	} catch ( e ) {
		// Not remembered.
	}
};

export default function initMenu() {
	const root = document.querySelector( '[data-menu-plan]' );
	if ( ! root ) {
		return;
	}
	const cfg = window.winonweb || {};
	const lang = cfg.lang || 'uk';
	const gram = cfg.grammar || {};
	const t = JSON.parse( root.dataset.i18n || '{}' );
	const daysEl = root.querySelector( '[data-menu-days]' );
	const empty = root.querySelector( '[data-menu-empty]' );
	const listBox = root.querySelector( '[data-menu-list]' );
	const itemsEl = root.querySelector( '[data-menu-items]' );
	const homeEl = root.querySelector( '[data-menu-home]' );
	const homeBox = root.querySelector( '[data-menu-home-box]' );
	const homeCount = root.querySelector( '[data-menu-home-count]' );
	const progress = root.querySelector( '[data-menu-progress]' );
	const status = root.querySelector( '[data-menu-status]' );
	const clearAll = root.querySelector( '[data-menu-clear]' );

	const recipes = new Map(); // group ID → data from the API.
	let checked = readChecked();

	const say = ( text ) => {
		status.textContent = '';
		window.setTimeout( () => {
			status.textContent = text;
		}, 40 );
	};

	const servingsText = ( n ) => {
		const rounded = Math.round( n * 2 ) / 2;
		return `${ formatQty( rounded, lang ) } ${
			unitFor( t.servingsForms, rounded, lang ) || ''
		}`.trim();
	};

	/* ---------------- Days ---------------- */

	const row = ( entry, index ) => {
		const r = recipes.get( entry.g );
		const li = document.createElement( 'li' );
		li.className = 'menu-item';
		if ( ! r ) {
			return null;
		}

		if ( r.img ) {
			const img = document.createElement( 'img' );
			img.src = r.img;
			img.alt = '';
			img.width = 88;
			img.height = 88;
			img.loading = 'lazy';
			img.className = 'menu-item__img';
			li.append( img );
		}

		const body = document.createElement( 'div' );
		body.className = 'menu-item__body';
		const a = document.createElement( 'a' );
		a.href = r.url;
		a.className = 'menu-item__title';
		a.textContent = r.title;
		body.append( a );

		// Servings stepper: ±1 portion (stored as a factor of the recipe's own yield).
		const base = Math.max( 1, Number( r.servings ) || 1 );
		const portions = Math.max( 1, Math.round( base * entry.f ) );
		const most = Math.max( MAX_PORTIONS, base * 4 );
		const serv = document.createElement( 'div' );
		serv.className = 'menu-item__servings';
		serv.setAttribute( 'role', 'group' );
		serv.setAttribute( 'aria-label', `${ t.servings }: ${ r.title }` );
		const minus = document.createElement( 'button' );
		minus.type = 'button';
		minus.textContent = '−';
		minus.setAttribute( 'aria-label', t.fewer );
		minus.disabled = portions <= 1;
		const out = document.createElement( 'output' );
		out.textContent = servingsText( portions );
		const plus = document.createElement( 'button' );
		plus.type = 'button';
		plus.textContent = '+';
		plus.setAttribute( 'aria-label', t.more );
		plus.disabled = portions >= most;
		const change = ( delta ) => {
			const list = readMenu();
			list[ index ].f =
				Math.min( most, Math.max( 1, portions + delta ) ) / base;
			writeMenu( list );
			render();
		};
		minus.addEventListener( 'click', () => change( -1 ) );
		plus.addEventListener( 'click', () => change( 1 ) );
		serv.append( minus, out, plus );
		li.append( body );

		// Servings and day share one row of controls under the title.
		const controls = document.createElement( 'div' );
		controls.className = 'menu-item__controls';
		controls.append( serv );

		// Day.
		const label = document.createElement( 'label' );
		label.className = 'menu-item__day';
		const sr = document.createElement( 'span' );
		sr.className = 'screen-reader-text';
		sr.textContent = `${ t.day }: ${ r.title }`;
		const select = document.createElement( 'select' );
		[ [ -1, t.noDay ], ...t.days.map( ( d, i ) => [ i, d ] ) ].forEach(
			( [ v, text ] ) => {
				const o = document.createElement( 'option' );
				o.value = String( v );
				o.textContent = text;
				o.selected = v === entry.d;
				select.append( o );
			}
		);
		select.addEventListener( 'change', () => {
			const list = readMenu();
			list[ index ].d = Number( select.value );
			writeMenu( list );
			render();
		} );
		label.append( sr, select );
		controls.append( label );
		li.append( controls );

		const remove = document.createElement( 'button' );
		remove.type = 'button';
		remove.className = 'menu-item__remove';
		remove.setAttribute(
			'aria-label',
			( t.remove || '{x}' ).replace( '{x}', r.title )
		);
		remove.textContent = '×';
		remove.addEventListener( 'click', () => {
			writeMenu( readMenu().filter( ( _, i ) => i !== index ) );
			render();
			say( t.removed );
		} );
		li.append( remove );
		return li;
	};

	const renderDays = ( menu ) => {
		const groups = [
			...t.days.map( ( name, d ) => ( { name, d } ) ),
			{ name: t.noDay, d: -1 },
		];
		daysEl.replaceChildren(
			...groups
				.map( ( { name, d } ) => {
					const rows = menu
						.map( ( e, i ) => ( e.d === d ? row( e, i ) : null ) )
						.filter( Boolean );
					if ( ! rows.length ) {
						return null;
					}
					const sec = document.createElement( 'section' );
					sec.className = 'menu-day';
					const h = document.createElement( 'h2' );
					h.className = 'menu-day__title';
					h.textContent = name;
					const ul = document.createElement( 'ul' );
					ul.className = 'menu-day__list';
					ul.setAttribute( 'role', 'list' );
					ul.append( ...rows );
					sec.append( h, ul );
					return sec;
				} )
				.filter( Boolean )
		);
	};

	/* ---------------- Shopping list ---------------- */

	const COUNT_UNITS = gram.count || [ 'шт.', 'pc', 'зубч.', 'clove', '' ];

	const combine = ( menu ) => {
		const lines = new Map();
		menu.forEach( ( e ) => {
			const r = recipes.get( e.g );
			if ( ! r ) {
				return;
			}
			r.items.forEach( ( item ) => {
				const line = lines.get( item.merge ) || {
					label: item.label,
					unit: item.unit,
					forms: item.forms,
					home: item.home,
					qty: 0,
					loose: false,
					key: item.merge,
				};
				if ( typeof item.qty === 'number' && item.qty > 0 ) {
					line.qty += item.qty * e.f;
				} else {
					line.loose = true;
				}
				lines.set( item.merge, line );
			} );
		} );
		// "Sour cream, 100 g" + "sour cream (to serve)" is one thing to buy: fold the
		// amount-less line into a measured line with the same name.
		const all = [ ...lines.values() ];
		const measured = new Map(
			all
				.filter( ( l ) => l.qty > 0 )
				.map( ( l ) => [ l.label.toLocaleLowerCase( lang ), l ] )
		);
		return all
			.filter( ( l ) => {
				const twin =
					! l.qty &&
					measured.get( l.label.toLocaleLowerCase( lang ) );
				if ( twin ) {
					twin.loose = true;
				}
				return ! twin;
			} )
			.sort( ( a, b ) => a.label.localeCompare( b.label, lang ) );
	};

	// 1000 g → 1 kg, 1500 ml → 1.5 l (shopping amounts, not cooking amounts).
	const BIG = { г: 'кг', мл: 'л', g: 'kg', ml: 'l' };
	const cap = ( s ) => s.charAt( 0 ).toLocaleUpperCase( lang ) + s.slice( 1 );

	const lineText = ( l ) => {
		const label = cap( l.label );
		if ( ! l.qty ) {
			return label;
		}
		let q = l.qty;
		let unit = l.forms ? null : l.unit;
		if ( COUNT_UNITS.includes( l.unit ) ) {
			q = Math.ceil( q * 2 ) / 2; // Whole things: round up to a half.
		} else if ( BIG[ l.unit ] && q >= 1000 ) {
			q = Math.ceil( q / 50 ) / 20; // To 0.05 kg / l.
			unit = BIG[ l.unit ];
		} else if ( BIG[ l.unit ] && q >= 3 ) {
			q = q >= 100 ? Math.ceil( q / 10 ) * 10 : Math.round( q );
		}
		if ( l.forms ) {
			unit = unitFor( l.forms, q, lang );
		}
		const amount =
			BIG[ l.unit ] && q < 10 && unit !== l.unit
				? String( q ).replace( '.', gram.decimal || ',' )
				: formatQty( q, lang );
		return `${ label } — ${ amount }${ unit ? ' ' + unit : '' }${
			l.loose ? ` + ${ t.toTaste }` : ''
		}`;
	};

	// How far through the shop the reader is. The bar is drawn from --done in
	// CSS, so the number stays the thing a screen reader announces.
	const showProgress = ( buy ) => {
		const done = buy.filter( ( l ) => checked.has( l.key ) ).length;
		progress.hidden = ! buy.length;
		progress.textContent = ( t.bought || '' )
			.replace( '{done}', String( done ) )
			.replace( '{total}', String( buy.length ) );
		progress.style.setProperty(
			'--done',
			buy.length ? String( done / buy.length ) : '0'
		);
		progress.dataset.full = String( buy.length > 0 && done === buy.length );
	};

	const renderList = ( menu ) => {
		const lines = combine( menu );
		const buy = lines.filter( ( l ) => ! l.home );
		const home = lines.filter( ( l ) => l.home );

		itemsEl.replaceChildren(
			...buy.map( ( l, i ) => {
				const li = document.createElement( 'li' );
				const id = `menu-line-${ i }`;
				const box = document.createElement( 'input' );
				box.type = 'checkbox';
				box.id = id;
				box.checked = checked.has( l.key );
				box.addEventListener( 'change', () => {
					if ( box.checked ) {
						checked.add( l.key );
					} else {
						checked.delete( l.key );
					}
					writeChecked( checked );
					showProgress( buy );
				} );
				const label = document.createElement( 'label' );
				label.htmlFor = id;
				label.textContent = lineText( l );
				li.append( box, label );
				return li;
			} )
		);
		homeEl.replaceChildren(
			...home.map( ( l ) =>
				Object.assign( document.createElement( 'li' ), {
					textContent: lineText( l ),
				} )
			)
		);
		homeCount.textContent = String( home.length );
		homeBox.hidden = ! home.length;
		listBox.hidden = ! buy.length;
		showProgress( buy );
		return { buy, home };
	};

	const listText = () => {
		const menu = readMenu();
		const { buy, home } = {
			buy: combine( menu ).filter(
				( l ) => ! l.home && ! checked.has( l.key )
			),
			home: combine( menu ).filter( ( l ) => l.home ),
		};
		const titles = menu
			.map( ( e ) => recipes.get( e.g ) )
			.filter( Boolean )
			.map( ( r ) => r.title );
		return [
			`${ t.listTitle } (${ titles.length })`,
			titles.join( ', ' ),
			'',
			...buy.map( ( l ) => `• ${ lineText( l ) }` ),
			...( home.length
				? [
						'',
						`${ t.atHome }: ${ home
							.map( ( l ) => l.label )
							.join( ', ' ) }`,
				  ]
				: [] ),
		].join( '\n' );
	};

	root.querySelector( '[data-menu-copy]' ).addEventListener(
		'click',
		async () => {
			const text = listText();
			try {
				await navigator.clipboard.writeText( text );
				say( t.copied );
			} catch ( e ) {
				say( t.copyFailed );
			}
		}
	);
	const share = root.querySelector( '[data-menu-share]' );
	if ( navigator.share ) {
		share.hidden = false;
		share.addEventListener( 'click', async () => {
			try {
				await navigator.share( {
					title: t.listTitle,
					text: listText(),
				} );
			} catch ( e ) {
				// Cancelled.
			}
		} );
	}
	root.querySelector( '[data-menu-uncheck]' ).addEventListener(
		'click',
		() => {
			checked = new Set();
			writeChecked( checked );
			render();
		}
	);
	clearAll.addEventListener( 'click', () => {
		writeMenu( [] );
		checked = new Set();
		writeChecked( checked );
		render();
		say( t.cleared );
	} );

	/* ---------------- Data ---------------- */

	function render() {
		const menu = readMenu();
		empty.hidden = menu.length > 0;
		clearAll.hidden = menu.length === 0;
		renderDays( menu );
		renderList( menu );
	}

	async function load() {
		const menu = readMenu();
		const missing = [ ...new Set( menu.map( ( e ) => e.g ) ) ].filter(
			( g ) => ! recipes.has( g )
		);
		if ( missing.length ) {
			try {
				const res = await fetch(
					`${ cfg.restCore }ingredients?ids=${ missing.join(
						','
					) }&lang=${ encodeURIComponent( cfg.lang || '' ) }`
				);
				const data = await res.json();
				( data.items || [] ).forEach( ( r ) =>
					recipes.set( Number( r.id ), r )
				);
			} catch ( e ) {
				say( t.failed );
			}
		}
		render();
	}

	window.addEventListener( 'storage', ( e ) => {
		if ( e.key === 'smakota-menu' ) {
			load();
		}
	} );
	load();
}
